import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './ItemForm.css';

/**
 * Form component for creating/editing records
 */
const ItemForm = ({ item, onSubmit, onCancel }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name_en: '',
    name_gr: '',
    carbs: '',
    fat: '',
    proteins: '',
    energy: '',
    grams: '',
    quantity: '',
    unit: 'Gramm',
  });

  const [errors, setErrors] = useState({});

  // Populate form with existing data (when editing)
  useEffect(() => {
    if (item) {
      setFormData({
        name_en: item.name_en || '',
        name_gr: item.name_gr || '',
        carbs: item.carbs || '',
        fat: item.fat || '',
        proteins: item.proteins || '',
        energy: item.energy || '',
        grams: item.grams || '',
        quantity: item.quantity || '',
        unit: item.unit || 'Gramm',
      });
    }
  }, [item]);

  /**
   * Handle input changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  /**
   * Validate form
   */
  const validate = () => {
    const newErrors = {};

    if (!formData.name_en.trim()) {
      newErrors.name_en = t('form.validation.nameEnRequired');
    }

    if (!formData.name_gr.trim()) {
      newErrors.name_gr = t('form.validation.nameGrRequired');
    }

    if (formData.carbs && isNaN(formData.carbs)) {
      newErrors.carbs = t('form.validation.carbsInvalid');
    }

    if (formData.fat && isNaN(formData.fat)) {
      newErrors.fat = t('form.validation.fatInvalid');
    }

    if (formData.proteins && isNaN(formData.proteins)) {
      newErrors.proteins = t('form.validation.proteinsInvalid');
    }

    if (formData.energy && isNaN(formData.energy)) {
      newErrors.energy = t('form.validation.energyInvalid');
    }

    if (formData.grams && isNaN(formData.grams)) {
      newErrors.grams = t('form.validation.gramsInvalid');
    }

    if (formData.quantity && isNaN(formData.quantity)) {
      newErrors.quantity = t('form.validation.quantityInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submit form
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      // Prepare data (convert numbers)
      const submitData = {
        ...formData,
        carbs: formData.carbs ? parseFloat(formData.carbs) : 0,
        fat: formData.fat ? parseFloat(formData.fat) : 0,
        proteins: formData.proteins ? parseFloat(formData.proteins) : 0,
        energy: formData.energy ? parseInt(formData.energy, 10) : 0,
        grams: formData.grams ? parseInt(formData.grams, 10) : 0,
        quantity: formData.quantity ? parseInt(formData.quantity, 10) : 0,
      };

      onSubmit(submitData);
    }
  };

  return (
    <div className="form-container">
      <h2>{item ? t('form.labels.edit') : t('form.labels.create')}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name_en">{t('form.labels.nameEn')} *</label>
          <input
            type="text"
            id="name_en"
            name="name_en"
            value={formData.name_en}
            onChange={handleChange}
            placeholder="e.g. Rice"
          />
          {errors.name_en && <div className="error">{errors.name_en}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="name_gr">{t('form.labels.nameGr')} *</label>
          <input
            type="text"
            id="name_gr"
            name="name_gr"
            value={formData.name_gr}
            onChange={handleChange}
            placeholder="e.g. Ρύζι"
          />
          {errors.name_gr && <div className="error">{errors.name_gr}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="carbs">{t('form.labels.carbs')}</label>
          <input
            type="text"
            id="carbs"
            name="carbs"
            value={formData.carbs}
            onChange={handleChange}
            placeholder="e.g. 25.5"
          />
          {errors.carbs && <div className="error">{errors.carbs}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="fat">{t('form.labels.fat')}</label>
          <input
            type="text"
            id="fat"
            name="fat"
            value={formData.fat}
            onChange={handleChange}
            placeholder="e.g. 10.5"
          />
          {errors.fat && <div className="error">{errors.fat}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="proteins">{t('form.labels.proteins')}</label>
          <input
            type="text"
            id="proteins"
            name="proteins"
            value={formData.proteins}
            onChange={handleChange}
            placeholder="e.g. 7.5"
          />
          {errors.proteins && <div className="error">{errors.proteins}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="energy">{t('form.labels.energy')}</label>
          <input
            type="text"
            id="energy"
            name="energy"
            value={formData.energy}
            onChange={handleChange}
            placeholder="e.g. 350"
          />
          {errors.energy && <div className="error">{errors.energy}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="grams">{t('form.labels.grams')}</label>
          <input
            type="text"
            id="grams"
            name="grams"
            value={formData.grams}
            onChange={handleChange}
            placeholder="e.g. 100"
          />
          {errors.grams && <div className="error">{errors.grams}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="quantity">{t('form.labels.quantity')}</label>
          <input
            type="text"
            id="quantity"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            placeholder="e.g. 1"
          />
          {errors.quantity && <div className="error">{errors.quantity}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="unit">{t('form.labels.unit')}</label>
          <select
            id="unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
          >
            <option value="Gramm">{t('table.units.Gramm')}</option>
            <option value="Becher">{t('table.units.Becher')}</option>
            <option value="Liter">{t('table.units.Liter')}</option>
          </select>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-success">
            {t('buttons.save')}
          </button>
          {item && (
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {t('buttons.cancel')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ItemForm;
